/**
 * Domain constants — ported verbatim from legacy FEATURES.md to preserve behavior.
 * Any change here is a behavior change; require explicit alignment.
 */

/** Workday model: 8h × ~20 days/month — used by health bars and workload UI. */
export const WORKDAY_HOURS = 8;
export const WORKDAYS_PER_MONTH = 20;

/** Monthly assignee cap — must stay in lockstep with the per-day feasibility warning (8h × 20d). */
export const TOTAL_MONTH_HOURS = WORKDAY_HOURS * WORKDAYS_PER_MONTH;

/** Gamification — point awards per task completion timing. */
export const GAMIFICATION_POINTS = {
  earlyCompletion: 18,
  onTimeCompletion: 12,
  lateCompletion: 4,
  undatedCompletion: 6,
} as const;

/** Tier thresholds (monthly peak points → tier 0/1/2/3). */
export const TIER_THRESHOLDS = [0, 30, 80, 150] as const;

/** Field-level validation caps (from legacy FEATURES.md §24). */
export const LIMITS = {
  maxPinnedClients: 3,
  maxCheckInRoundsPerClient: 200,
  companyNoteBodyMax: 32_000,
  checkInQuestionMax: 500,
  taskTitleMax: 200,
  taskNotesMax: 1_000,
  commentTextMax: 1_000,
  interruptLabelMax: 200,
  tierEmojiMax: 8,
  tierLabelMax: 40,
} as const;

/** Email notification event names — must match server NOTIFY_EVENTS. */
export const NOTIFY_EVENTS = [
  'taskAssigned',
  'taskCompleted',
  'taskComment',
  'taskCreated',
  'companyCheckInReminder',
  'callBooked',
  'callBookingReceived',
  'callInviteeConfirmation',
  'callInviteeVideoLink',
  'callCancelled',
  'callCancelledInvitee',
] as const;
export type NotifyEvent = (typeof NOTIFY_EVENTS)[number];

/** Login rate-limit (per IP + email). */
export const LOGIN_RATE_LIMIT = {
  windowMs: 15 * 60 * 1000, // 15 min
  maxAttempts: 10,
  blockMs: 30 * 60 * 1000, // 30 min
} as const;

/** Session lifetime. */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Standalone dashboard PIN session (not tied to user accounts). */
export const DASHBOARD_SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Dashboard PIN rate-limit (per IP). */
export const DASHBOARD_PIN_RATE_LIMIT = {
  windowMs: 15 * 60 * 1000,
  maxAttempts: 8,
  blockMs: 15 * 60 * 1000,
} as const;

/** User membership types. */
export const MEMBERSHIP_TYPES = ['employee', 'company'] as const;
export type MembershipType = (typeof MEMBERSHIP_TYPES)[number];

/** Project view modes per page. */
export const PROJECT_VIEW_MODES = ['cards', 'list'] as const;
export type ProjectViewMode = (typeof PROJECT_VIEW_MODES)[number];

/** Taxation modes for Management page. */
export const TAXATION_MODES = ['USN6', 'USN15', 'AUSN8'] as const;
export type TaxationMode = (typeof TAXATION_MODES)[number];

/** Internal cost basis for pro bono hours on the Management KPI row. */
export const PRO_BONO_HOUR_COST = 750;
